import { useEffect, useState } from 'react';
import type { Client } from '@repo/api-client/dtos';
import { useCreateClient } from '@repo/queries';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useToast } from '@/components/ui/use-toast';
import { describeAppError } from '@/features/common/describeAppError';

interface QuickAddClientDialogProps {
  open: boolean;
  onOpenChange: (next: boolean) => void;
  /**
   * Fires after the server returns the freshly created Client. The
   * caller is responsible for whatever happens next (typically:
   * auto-select the new client in the parent form).
   */
  onCreated: (client: Client) => void;
}

/**
 * Lightweight "name + email" client capture so the user can add a
 * recipient without leaving the invoice form. Heavier fields
 * (company, address, tax id, etc.) stay on the dedicated Client
 * detail screen — this dialog only captures the minimum the wire
 * needs (`name` is the only required field; we surface email since
 * it's used by the Send flow later).
 */
export function QuickAddClientDialog({
  open,
  onOpenChange,
  onCreated,
}: QuickAddClientDialogProps) {
  const { createClient, loading } = useCreateClient();
  const { toast } = useToast();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [nameError, setNameError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setName('');
      setEmail('');
      setNameError(null);
    }
  }, [open]);

  async function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const trimmedName = name.trim();
    if (trimmedName === '') {
      setNameError('Required');
      return;
    }
    try {
      const trimmedEmail = email.trim();
      const created = await createClient({
        name: trimmedName,
        contact: trimmedEmail ? { email: trimmedEmail } : {},
        defaultTaxExempt: false,
        isActive: true,
      });
      toast({
        title: 'Client added',
        description: `${created.name} is ready.`,
        variant: 'success',
      });
      onCreated(created);
      onOpenChange(false);
    } catch (err) {
      const desc = describeAppError(err);
      toast({ title: desc.title, description: desc.message, variant: 'danger' });
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Add a client</DialogTitle>
          <DialogDescription>
            Just the basics for now. You can fill in company, address, and tax
            details later from the client's page.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="flex flex-col gap-3">
          <div className="flex flex-col gap-1.5">
            <Label
              htmlFor="quick-client-name"
              className="text-[11px] font-semibold uppercase tracking-[0.16em] text-muted-foreground"
            >
              Name
            </Label>
            <Input
              id="quick-client-name"
              value={name}
              onChange={(e) => {
                setName(e.target.value);
                if (nameError) setNameError(null);
              }}
              placeholder="Acme Co. or Jane Doe"
              autoFocus
              invalid={!!nameError}
            />
            {nameError ? <p className="text-[11px] text-destructive">{nameError}</p> : null}
          </div>

          <div className="flex flex-col gap-1.5">
            <Label
              htmlFor="quick-client-email"
              className="text-[11px] font-semibold uppercase tracking-[0.16em] text-muted-foreground"
            >
              Email <span className="font-normal normal-case tracking-normal text-muted-foreground">(optional)</span>
            </Label>
            <Input
              id="quick-client-email"
              type="email"
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="them@example.com"
            />
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" loading={loading}>
              Add client
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
